import React from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { createAopPlan } from "../../Services/Brokerage";
import { useNavigate } from "react-router-dom";
import { errorTost, successTost } from "../../Services/Toast";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
/* ---------------- STEPS ---------------- */
const steps = [
  { label: "Validity Period", sub: "Financial year" },
  { label: "Calculation Rules", sub: "Define slabs" },
  { label: "Eligibility", sub: "Optional constraints" },
  { label: "CP Sign-Off", sub: "Configure window" },
  { label: "Exclusions", sub: "POP projects" },
  { label: "Review", sub: "Preview & save" },
];

export default function AOPReviewConfiguration({ data, onBack, onNext }) {
  const {
      financialYear,
  isActive,
  targetBasis,
  incentiveType,
  targetSlabs = [],
    eligibility,
    signOffRequired,
    signOffStartDate,
    signOffEndDate,
    popExclusions = [],
  } = data;
  const navigate = useNavigate();
  const [file, setFile] = React.useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const slabCount = targetSlabs.length;

  const maxBrokerage =
    incentiveType === "percentage"
      ? Math.max(...targetSlabs.map((s) => Number(s.incentive || 0)), 0) + "%"
      : "₹" + Math.max(...targetSlabs.map((s) => Number(s.incentive || 0)), 0);

  /* ---------------- ACTIVATE AOP ---------------- */
  const handleActivate = async () => {
    try {
      const payload = {
        ...data,
        file: file,
      };

      const res = await createAopPlan(payload);

      if (!res.success) {
        errorTost(res.error?.message || "Failed to activate AOP");
        return;
      }

      successTost("AOP activated successfully");

      navigate("/home/BenefitPlan", { state: { timestamp: Date.now() } });
    } catch (error) {
      console.error("Activate AOP Error:", error);
      errorTost("Something went wrong while activating AOP");
    }
  };

  return (
    <Box sx={{ p: 1, minHeight: "100vh" }}>
      {/* Back */}
      <Typography
        variant="body2"
        sx={{
          mb: 3,
          cursor: "pointer",
          color: "text.secondary",
          fontSize: 14,
          fontWeight: 500,
        }}
        onClick={onBack}
      >
        ← Back to AOPs
      </Typography>

      {/* Stepper */}
      <Stepper
        activeStep={5}
        alternativeLabel
        sx={{
          mb: 6,
          "& .MuiStepLabel-label": {
            fontSize: "0.95rem",
            fontWeight: 500,
          },
          "& .MuiStepIcon-root": {
            fontSize: 32,
          },
          "& .Mui-active": {
            color: "#2563EB",
          },
          "& .Mui-completed": {
            color: "#2563EB",
          },
        }}
      >
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel>
              <Box textAlign="center">
                <Typography fontSize="0.95rem" fontWeight={600}>
                  {step.label}
                </Typography>
                <Typography fontSize="0.75rem" color="text.secondary">
                  {step.sub}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Info */}
      <Box
        sx={{
          background: "#EFF6FF",
          border: "1px solid #BFDBFE",
          borderRadius: 2,
          p: 2,
          display: "flex",
          gap: 1.5,
          mb: 3,
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#2563EB" }} />
        <Typography fontSize={13} color="#1E3A8A">
          AOP achievement is calculated only on successful agreements.
        </Typography>
      </Box>

      {/* Review Card */}
      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          boxShadow: "none",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography fontSize={16} fontWeight={600}>
            Review AOP Configuration
          </Typography>
        </Box>

        {/* Summary */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 2,
            mb: 3,
          }}
        >
          <SummaryCard title="Financial Year" value={financialYear} />

          <SummaryCard
            title="Slabs Configured"
            value={`${slabCount} tier${slabCount !== 1 ? "s" : ""}`}
          />

          <SummaryCard
            title="Sign-Off Window"
            value={
              signOffRequired
                ? `${signOffStartDate} → ${signOffEndDate}`
                : "Not Required"
            }
          />

          <SummaryCard title="Max Brokerage" value={maxBrokerage} />

          <SummaryCard
            title="Status"
            value={
              <Chip
                label={isActive ? "Active" : "Inactive"}
                size="small"
                color={isActive ? "success" : "default"}
              />
            }
          />

          <SummaryCard
            title="Excluded Projects"
            value={`${popExclusions.length} (POP)`}
          />
        </Box>

        {/* Eligibility */}
        {eligibility && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography fontSize={14} fontWeight={600} mb={1}>
              Eligibility Conditions
            </Typography>

            <Typography fontSize={12}>
              Site Visits:{" "}
              {eligibility.siteVisitEnabled
                ? `${eligibility.siteVisitMin || 0} – ${
                    eligibility.siteVisitMax || "∞"
                  }`
                : "Not Applied"}
            </Typography>

            <Typography fontSize={12}>
              FOS Count:{" "}
              {eligibility.fosEnabled
                ? `${eligibility.fosMin || 0} – ${eligibility.fosMax || "∞"}`
                : "Not Applied"}
            </Typography>
          </>
        )}
        <Box mt={3}>
          <Typography fontSize={14} fontWeight={600} mb={1}>
            Upload AOP Document
          </Typography>

          <Button
            variant="outlined"
            component="label"
            startIcon={<DescriptionOutlinedIcon />}
          >
            Upload Document
            <input
              type="file"
              hidden
              accept=".pdf"
              onChange={(e) => {
                const selectedFile = e.target.files[0];
                if (!selectedFile) return;

                setFile(selectedFile);
              }}
            />
          </Button>

          {file && (
            <Typography fontSize={12} mt={1}>
              Selected File: {file.name}
            </Typography>
          )}
        </Box>
      </Card>

      {/* Footer */}
      <Box display="flex" justifyContent="space-between" mt={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          startIcon={<CheckCircleIcon />}
          onClick={handleActivate}
          disabled={!file}
        >
          Activate AOP
        </Button>
      </Box>
    </Box>
  );
}

/* ---------------- SUMMARY CARD ---------------- */

function SummaryCard({ title, value }) {
  return (
    <Box
      sx={{
        background: "#F1F5F9",
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography fontSize={11} color="#64748B">
        {title.toUpperCase()}
      </Typography>
      <Typography fontSize={14} fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}
